import { Controller, useFormContext } from "react-hook-form";

import { useModelProfiles } from "@/shared/domain/agentThread/hooks/useModelProfiles";
import {
  FormSection,
  Toggle,
  ToggleGroup,
} from "@/shared/foundation/primitives";
import { agentFormSectionId } from "@/shared/navigation";

import { type AgentFormValues } from "../agentFormSchema";

import styles from "./sections.module.css";

/** Toggle value standing for `null`: no model of its own, follow the org's default. */
const ORG_DEFAULT_VALUE = "__org_default__";

/**
 * Which model the agent reasons with. The form holds `null` for "whatever the
 * org has set as its default", which is what an untouched agent keeps and what
 * the "Org default" toggle returns it to; an agent on `null` follows the org
 * when its default changes. `data-profile` reports the profile the agent will
 * actually run on either way.
 */
export function ModelSection() {
  const { control } = useFormContext<AgentFormValues>();
  // The heading's id is both what the toggle group is named by and what an
  // edit link's hash lands on.
  const titleId = agentFormSectionId("model");
  const profilesQuery = useModelProfiles();
  const profiles = profilesQuery.data ?? [];
  const orgDefault =
    profiles.find((profile) => profile.is_default)?.id ??
    profiles[0]?.id ??
    null;

  return (
    <FormSection
      title="What model to use"
      titleId={titleId}
      hint="Balance speed and cost against how much reasoning the goals need."
    >
      <Controller
        control={control}
        name="modelProfile"
        render={({ field }) => {
          const chosen = field.value ?? orgDefault;
          const described = profiles.find((profile) => profile.id === chosen);
          const orgDefaultLabel = profiles.find(
            (profile) => profile.id === orgDefault,
          )?.label;
          return (
            <div data-testid="agent-model" data-profile={chosen ?? ""}>
              <ToggleGroup
                aria-labelledby={titleId}
                value={[field.value ?? ORG_DEFAULT_VALUE]}
                onValueChange={([next]) => {
                  // Base UI holds the value as an array, so pressing the
                  // chosen toggle again would clear it. A choice is not
                  // optional, so that change is ignored.
                  if (next === undefined) {
                    return;
                  }
                  field.onChange(next === ORG_DEFAULT_VALUE ? null : next);
                }}
              >
                <Toggle value={ORG_DEFAULT_VALUE}>
                  {orgDefaultLabel === undefined
                    ? "Org default"
                    : `Org default (${orgDefaultLabel})`}
                </Toggle>
                {profiles.map((profile) => (
                  <Toggle key={profile.id} value={profile.id}>
                    {profile.label}
                  </Toggle>
                ))}
              </ToggleGroup>
              {described !== undefined && (
                <p className={styles.modelDescription}>
                  {described.description}
                </p>
              )}
              {profilesQuery.isPending && (
                <p className={styles.muted}>Loading models&hellip;</p>
              )}
            </div>
          );
        }}
      />
    </FormSection>
  );
}
